import type { ManagedKpiDetail } from "../api/types.js";
import { parseObservationValue } from "./kpi-observation-value.js";

const DAY_MS = 86_400_000;

/** Up to four decimals — the platform's DECIMAL(18,4) bound on recorded values
 * — with trailing zeros trimmed, so no accepted nonzero gap ever reads as 0. */
function formatAmount(value: number): string {
  return Number.isInteger(value)
    ? String(value)
    : value.toFixed(4).replace(/\.?0+$/, "");
}

/**
 * The sentence under the target cells, derived from the data on screen and
 * never stored: which way counts as improvement, how far the current reading
 * sits from the target, and how long is left. Each part is null when the fact
 * behind it is missing, so nothing is invented.
 */
export function targetProgress(
  kpi: ManagedKpiDetail,
  now: number = Date.now(),
): { readonly gap: string | null; readonly timeLeft: string | null } {
  const target = kpi.currentTarget;
  if (!target) return { gap: null, timeLeft: null };
  const current = parseObservationValue(kpi.currentState?.currentValue ?? "");
  const goal = parseObservationValue(target.targetValue);
  let gap: string | null = null;
  if (current !== null && goal !== null) {
    const diff = goal - current;
    const unitWord = kpi.unit === "%" ? "points" : kpi.unit;
    const amount = `${formatAmount(Math.abs(diff))}${unitWord ? ` ${unitWord}` : ""}`;
    gap =
      diff === 0
        ? "on target"
        : `${amount} ${current < goal ? "below" : "above"} target`;
  }
  let timeLeft: string | null = null;
  if (target.targetDate) {
    const due = Date.parse(`${target.targetDate}T00:00:00.000Z`);
    if (!Number.isNaN(due)) {
      const days = Math.ceil((due - now) / DAY_MS);
      const months = Math.round(days / 30.44);
      timeLeft =
        days < 0
          ? "past its target date"
          : months >= 2
            ? `with ${months} months to go`
            : months === 1
              ? "with a month to go"
              : `with ${days} ${days === 1 ? "day" : "days"} to go`;
    }
  }
  return { gap, timeLeft };
}
